import { cn } from '@wellesley/ui'
import { FC, PropsWithChildren } from 'react'
import {
  Ban,
  Info,
  CircleCheck,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react'

const BADGE_ICON_SIZE = 14

const variantConfig = {
  success: {
    class: 'bg-green-100 text-green-700',
    icon: CircleCheck,
  },
  warning: {
    class: 'bg-yellow-100 text-yellow-700',
    icon: TriangleAlert,
  },
  error: {
    class: 'bg-red-100 text-red-700',
    icon: Ban,
  },
  neutral: {
    class: 'bg-gray-100 text-gray-700',
  },
  brand: {
    class: 'bg-blue-100 text-blue-700',
    icon: Info,
  },
  fuchsia: {
    class: 'bg-fuchsia-100 text-fuchsia-700',
  },
  violet: {
    class: 'bg-violet-100 text-violet-700',
  },
  pink: {
    class: 'bg-pink-100 text-pink-700',
  },
  cyan: {
    class: 'bg-cyan-100 text-cyan-700',
  },
  disabled: {
    class: 'bg-gray-100 text-gray-300',
  },
} as const

export type BadgeVariant = keyof typeof variantConfig

type BadgeProps = PropsWithChildren & {
  variant: BadgeVariant
  icon?: React.ReactNode
  showIcon?: boolean
  className?: string
  'data-testid'?: string
}

export const Badge: FC<BadgeProps> = ({
  icon,
  variant,
  className,
  children,
  showIcon = true,
  'data-testid': dataTestId,
}) => {
  const config = variant ? variantConfig[variant] : null
  const ConfigIcon: LucideIcon | undefined =
    config != null && 'icon' in config ? (config.icon as LucideIcon) : undefined

  const iconToRender =
    icon ??
    (ConfigIcon ? (
      <ConfigIcon
        size={BADGE_ICON_SIZE}
        className="shrink-0 text-current"
        aria-hidden={false}
      />
    ) : undefined)

  const baseClasses =
    'flex w-fit items-center px-xxs py-xxxs rounded-sm text-xs font-medium gap-xxxs self-baseline'

  return (
    <span
      className={cn(baseClasses, config?.class, className)}
      data-testid={dataTestId}
    >
      {showIcon && iconToRender && iconToRender}
      {children}
    </span>
  )
}
